import clsx from 'clsx';
import { useTranslation } from 'next-i18next/pages';
import type { ComponentType } from 'react';
import { FiZap } from 'react-icons/fi';

import { Stack, Text, UnstyledButton } from '@highlight-cp/core-ui';

import classes from './prompt-button.module.css';

export interface PromptButtonProps {
  label: string;
  /** Optional icon component (e.g. react-icons); receives `size` from this control. */
  icon?: ComponentType<{ size?: string | number }>;
  value: string;
  isSelected: boolean;
  onClick: () => void;
  disableWhenSelected?: boolean;
  /** When set, used as `aria-label` instead of the default “Insert Prompt” i18n string. */
  accessibilityLabel?: string;
  /** When set, forwarded as `aria-pressed` for toggle-style selection. */
  ariaPressed?: boolean;
  maw?: number;
}

/**
 * ### PromptButton
 *
 * @description
 * Card-style control with a short title and supporting line, used for AI prompt
 * suggestions and similar two-line choices.
 *
 * @param props - Component props
 * @returns React component
 */
const PromptButton = ({
  label,
  icon: Icon,
  value,
  isSelected,
  onClick,
  disableWhenSelected = true,
  accessibilityLabel,
  ariaPressed,
  maw,
}: PromptButtonProps) => {
  const { t } = useTranslation();
  const ariaLabel = accessibilityLabel ?? t('Insert Prompt');

  return (
    <UnstyledButton
      aria-label={ariaLabel}
      aria-pressed={ariaPressed}
      className={clsx(
        classes.promptButton,
        isSelected && classes.selected,
        isSelected && disableWhenSelected && classes.selectedNonInteractive,
      )}
      onClick={isSelected && disableWhenSelected ? undefined : onClick}
      maw={maw}
    >
      <Stack gap="xs" align="center" h="100%" justify="center">
        {Icon ? <Icon size="20" /> : <FiZap size="20" />}
        <Stack justify="center" className={classes.textContainer}>
          <Text ta="center">{label}</Text>
          <Text size="sm" ta="center">
            {value}
          </Text>
        </Stack>
      </Stack>
    </UnstyledButton>
  );
};

export default PromptButton;
